import React, { useState } from "react";
import LegalHeader from "../components/LegalHeader";
import Footer from "../components/Footer";

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState<string>("intro");

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const sections = [
    { id: "intro", title: "1. Introduction & Overview" },
    { id: "data-collected", title: "2. Information We Collect" },
    { id: "android-permissions", title: "3. App Permissions & Play Store Policy" },
    { id: "how-we-use", title: "4. How We Use Information" },
    { id: "data-sharing", title: "5. Information Sharing & Disclosure" },
    { id: "security-retention", title: "6. Data Security & Storage" },
    { id: "account-deletion", title: "7. Account & Data Deletion" },
    { id: "cookies", title: "8. Cookies & Web Tracking" },
    { id: "children", title: "9. Children's Privacy" },
    { id: "updates", title: "10. Policy Updates" },
    { id: "grievance", title: "11. Grievance Redressal Officer" },
  ];

  return (
    <div className="w-full bg-white min-h-screen flex flex-col font-['Roboto',sans-serif]">
      <LegalHeader />

      {/* Header Title Section */}
      <section className="border-b border-gray-200 py-10 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-xs text-gray-500 mb-2">Fixezy / Legal / Privacy Policy</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Last updated: October 2026 • Applicable to Fixezy Customer App, Partner App, and fixezy.in
          </p>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-[1100px] mx-auto px-6 py-10 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Table of Contents - Clean simple text links */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28 border border-gray-200 rounded-lg p-5 bg-white">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">
              Table of Contents
            </p>
            <nav className="flex flex-col space-y-2">
              {sections.map((section) => (
                <p
                  key={section.id}
                  onClick={() => scrollTo(section.id)}
                  className={`cursor-pointer text-xs transition-colors ${activeSection === section.id
                      ? "text-black font-semibold"
                      : "text-gray-600 hover:text-black"
                    }`}
                >
                  {section.title}
                </p>
              ))}
            </nav>
          </aside>

          {/* Legal Text Area */}
          <main className="lg:col-span-8 text-gray-700 space-y-10 leading-relaxed text-sm sm:text-base">
            {/* Section 1 */}
            <section id="intro" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                1. Introduction & Overview
              </h2>
              <p>
                Fixezy Private Limited ("Fixezy", "we", "us", or "our"), located in Vijayanagar, Bengaluru, Karnataka, India, operates an on-demand home service marketplace connecting customers with background-verified independent service professionals for appliance repair, plumbing, electrical maintenance, cleaning, and related home services.
              </p>
              <p className="mt-3">
                This Privacy Policy describes our practices regarding the collection, storage, use, and disclosure of personal data through the Fixezy mobile application (available on Google Play Store and iOS App Store), our website (fixezy.in), and customer support channels.
              </p>
              <p className="mt-3 text-xs sm:text-sm text-gray-600">
                This policy complies with the Information Technology Act, 2000, Consumer Protection (E-Commerce) Rules, 2020, India's Digital Personal Data Protection (DPDP) Act, 2023, and Google Play Store Developer Program Policies.
              </p>
            </section>

            {/* Section 2 */}
            <section id="data-collected" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                2. Information We Collect
              </h2>
              <p>
                We collect information necessary to create your account, fulfill your service bookings, process payments, and ensure mutual safety:
              </p>
              <ul className="list-disc list-inside mt-3 space-y-2 text-gray-700">
                <li>
                  <strong>Personal Information:</strong> Name, verified mobile phone number (authenticated via SMS OTP), and email address.
                </li>
                <li>
                  <strong>Service Address:</strong> Flat/house number, building/apartment name, street address, locality, landmark, city, and postal PIN code.
                </li>
                <li>
                  <strong>Service Records:</strong> Appliance details (brand, model, issue description), appointment schedule, technician diagnostic notes, and service ratings.
                </li>
                <li>
                  <strong>Payment Data:</strong> Transactions are handled through RBI-regulated, PCI-DSS compliant payment gateways. Fixezy does not store debit/credit card numbers or CVV.
                </li>
              </ul>
            </section>

            {/* Section 3 - Google Play Compliance */}
            <section id="android-permissions" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                3. App Permissions & Google Play Store Policy
              </h2>
              <p>
                In compliance with Google Play Developer User Data policies, the app requests the following system permissions solely to deliver essential service functions:
              </p>

              <div className="overflow-x-auto border border-gray-200 rounded-lg mt-4">
                <table className="w-full text-left text-xs sm:text-sm">
                  <thead className="bg-gray-50 text-gray-900 font-semibold border-b border-gray-200">
                    <tr>
                      <th className="p-3">Permission</th>
                      <th className="p-3">Data Used</th>
                      <th className="p-3">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-gray-700">
                    <tr>
                      <td className="p-3 font-mono text-xs">Location (Fine / Coarse)</td>
                      <td className="p-3">GPS coordinates</td>
                      <td className="p-3">To identify service address, match nearby service partners, and show arrival status. Background location is not tracked for customers when the app is closed.</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">Camera & Photos</td>
                      <td className="p-3">Camera capture & device gallery</td>
                      <td className="p-3">Allows users to take or upload photos of broken appliances or leaks so technicians can assess the issue. Optional and requested only upon selection.</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">SMS / Phone</td>
                      <td className="p-3">Automated OTP & masked calling</td>
                      <td className="p-3">Uses Google Play SMS Retriever API for one-touch login OTP. Enables masked phone calls between user and technician without sharing private phone numbers.</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">Notifications</td>
                      <td className="p-3">Push alerts</td>
                      <td className="p-3">To send booking confirmation, partner dispatch status, and warranty updates. Can be toggled off in device settings.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 4 */}
            <section id="how-we-use" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                4. How We Use Information
              </h2>
              <p>We process personal data for legitimate business purposes:</p>
              <ul className="list-disc list-inside mt-3 space-y-1.5 text-gray-700">
                <li>Dispatching verified technicians to your address and completing doorstep repairs.</li>
                <li>Honoring our Fixezy 30-Day Service Warranty on eligible repair jobs.</li>
                <li>Managing customer support inquiries, rescheduling, and billing queries.</li>
                <li>Preventing spam accounts, fraudulent orders, and ensuring safety on both sides.</li>
                <li>Issuing GST-compliant invoices as mandated by Indian tax authorities.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="data-sharing" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                5. Information Sharing & Disclosure
              </h2>
              <p>
                We do not sell, rent, or trade your personal data. We disclose information strictly as follows:
              </p>
              <ul className="list-disc list-inside mt-3 space-y-2 text-gray-700">
                <li>
                  <strong>Assigned Service Partners:</strong> Once an order is confirmed, the technician receives your name, service address, and uploaded photos solely to perform the work. Technicians do not receive your payment details or booking history.
                </li>
                <li>
                  <strong>Service Providers:</strong> Payment processors, SMS gateways, and secure cloud infrastructure hosted in India.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> If mandated by court orders, government authorities, or law enforcement under Indian law.
                </li>
              </ul>
            </section>

            {/* Section 6 */}
            <section id="security-retention" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                6. Data Security & Storage
              </h2>
              <p>
                We employ standard administrative and technical safeguards:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-700">
                <li>Encryption in transit using TLS 1.3 cryptographic protocols.</li>
                <li>Encryption at rest using AES-256 standards on cloud servers located in India.</li>
                <li>Role-based access controls restricting data access to authorized personnel only.</li>
              </ul>
            </section>

            {/* Section 7 - Account Deletion */}
            <section id="account-deletion" className="scroll-mt-28 border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                7. Account & Data Deletion (Google Play Compliance)
              </h2>
              <p>
                In compliance with Google Play Store policies, users can request full deletion of their account and associated personal data at any time:
              </p>
              <div className="mt-3 space-y-3 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <p><strong>Method 1 (In-App):</strong> Go to Account / Profile → Settings → Privacy & Security → tap "Delete My Account", then confirm via OTP.</p>
                <p><strong>Method 2 (Email):</strong> Send an email to <strong>support@fixezy.in</strong> with the subject "Account Deletion Request" from your registered email address or mentioning your registered mobile number.</p>
                <p className="text-xs sm:text-sm text-gray-600">
                  Upon receiving your request, your personal identifiers and addresses are purged from active databases within 30 days. Historical tax invoices are retained in archived storage for the statutory period (7 years) as required under Indian Goods and Services Tax law.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section id="cookies" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                8. Cookies & Web Tracking
              </h2>
              <p>
                The website uses essential cookies to remember your location preferences and maintain active sessions. You can configure your browser to reject non-essential cookies at any time.
              </p>
            </section>

            {/* Section 9 */}
            <section id="children" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                9. Children's Privacy
              </h2>
              <p>
                Our services are intended only for individuals aged 18 and older. We do not knowingly collect personal data from minors. If we discover that a minor has provided information, we delete it immediately.
              </p>
            </section>

            {/* Section 10 */}
            <section id="updates" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                10. Policy Updates
              </h2>
              <p>
                We may revise this Privacy Policy periodically. Any significant revisions will be indicated by updating the "Last updated" date at the top of this page and through in-app notices where appropriate.
              </p>
            </section>

            {/* Section 11 - Grievance Officer */}
            <section id="grievance" className="scroll-mt-28 border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                11. Grievance Redressal Officer
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                In accordance with the Information Technology Act 2000 and the Consumer Protection (E-Commerce) Rules 2020:
              </p>
              <div className="border border-gray-200 rounded-lg p-4 space-y-1 text-sm text-gray-800">
                <p><strong>Designation:</strong> Grievance Redressal Officer</p>
                <p><strong>Entity:</strong> Fixezy Private Limited</p>
                <p><strong>Address:</strong> Vijayanagar, Bengaluru, Karnataka - 560040, India</p>
                <p><strong>Email:</strong> support@fixezy.in</p>
                <p><strong>Working Hours:</strong> Mon – Fri, 9:00 AM – 6:00 PM IST</p>
                <p className="text-xs text-gray-500 pt-1">Grievances are acknowledged within 48 hours and resolved within 30 days.</p>
              </div>
            </section>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
